import * as fp from 'lodash/fp';
import { createSelector, type Selector } from 'reselect';
import type { AppState } from 'src/reducers';
import type { BasicPivotItem, TreePivot, WorklistInfo } from 'src/worker/pivotWorker.types';
import type { CardViewDataInputs, CardViewOptionalInputs } from 'src/components/StandardCardView/UIData.types';
import type { SubheaderSlice } from 'src/components/Subheader/Subheader.slice';
import { cascadingFilter } from 'src/utils/Tree/ObjectArrayTree';
import { makelookBackPredicate } from 'src/utils/Pivot/Filter';
import { maybeSortPivotItems } from 'src/utils/Pivot/Sort';
import type { TopPerformersSlice } from './TopPerformers.slice';
import processDataForCardView from 'src/components/StandardCardView/CardViewDataProcessor';
import { getDataFromCache } from 'src/services/pivotServiceCache';

const getView: Selector<AppState, TopPerformersSlice> = (state) => {
  return state.pages.hindsighting.topPerformers;
};

const getStyles: Selector<AppState, TreePivot> = (state) => {
  return getDataFromCache(state.pages.hindsighting.topPerformers)?.tree || [];
};

const getSubheader: Selector<AppState, SubheaderSlice> = (state) => {
  return state.subheader;
};
const getWorklist: Selector<AppState, WorklistInfo[]> = (state) => {
  return state.pages.hindsighting.styleColorReview.sharedData.worklist;
};

const sortItems = (subh: SubheaderSlice, list: BasicPivotItem[]) => {
  return fp.flow(
    () => list,
    (viewItems): BasicPivotItem[] => {
      return maybeSortPivotItems(subh.sortBy, viewItems);
    },
    (sortedViewItems) => {
      const max = subh.countLimit;
      return max ? fp.slice(0, max, sortedViewItems) : sortedViewItems;
    },
  )();
};
export const getProcessedData = createSelector(
  getStyles,
  getView,
  getSubheader,
  getWorklist,
  (styles, viewConfig, subheader, worklist) => {
    if (styles && viewConfig && subheader) {
      const { lookBackPeriod } = subheader;
      let data = styles;

      if (lookBackPeriod) {
        data = cascadingFilter(styles, makelookBackPredicate(lookBackPeriod));
      }

      const dataInputs: CardViewDataInputs = {
        styles: sortItems(subheader, data),
        subheader,
        defns: {
          view: viewConfig.viewDefn || {},
          rollup: viewConfig.calcViewDefn || {},
        },
      };
      const optionalInputs: CardViewOptionalInputs = {
        worklist,
        filterFlowStatus: false,
      };

      return processDataForCardView(dataInputs, optionalInputs);
    }
    return [];
  },
);
